import {Image} from '../Image';
import React, {useMemo} from 'react';
import {useSetSelector} from '../../hooks/set-selector';
import {CameraBrandModel} from '../../types/control';
import {Configs} from '../../configs';

type Props = {
    id: string;
    quality: 360 | 720;
    className?: string;
    mode?: 'auto' | 'snapshot' | 'rtsp' | 'video_cgi' | 'mjpeg';
    brand?: CameraBrandModel;
}

type RendererProps = {
    ip: string;
    port?: number;
} & Props;

export function VideoCGIRenderer(props: RendererProps) {
    const subUrl = useMemo(() => {
        const urls: { [key: number]: string } = {};

        if (props.brand.video_cgi.stream.q_720p) {
            urls[720] = props.brand.video_cgi.stream.q_720p;
        }

        if (props.brand.video_cgi.stream.q_360p) {
            urls[360] = props.brand.video_cgi.stream.q_360p;
        }

        if (props.quality === 720) {
            return urls[720] || urls[360];
        }

        return urls[360] || urls[720];
    }, [props.brand, props.quality]);

    const url = 'http://' + props.ip + ':' + props.port;

    return (
        <Image
            className={props.className}
            src={url + subUrl}
            alt=""/>
    );
}

export function MJPEGRenderer(props: RendererProps) {
    const quality = useMemo(() => {
        const urls: { [key: number]: number } = {};

        if (props.brand.mjpeg.stream.q_720p) {
            urls[720] = 720;
        }

        if (props.brand.mjpeg.stream.q_360p) {
            urls[360] = 360;
        }

        if (props.quality === 720) {
            return urls[720] || urls[360];
        }

        return urls[360] || urls[720];
    }, [props.brand, props.quality]);
    const url = `${Configs.IMAGE_PREFIX}/camera/mjpeg/${props.id}/${quality}`;

    return (
        <Image
            className={props.className}
            src={url}
            alt=""
            interval={props.brand.mjpeg.delay_ms}
        />
    );
}

export function RTSPRenderer(props: RendererProps) {
    const url = `${Configs.IMAGE_PREFIX}/camera/rtsp/${props.id}`;

    return (
        <Image
            className={props.className}
            src={url}
            alt=""
        />
    );
}

export function SnapshotRenderer(props: RendererProps) {
    const random = useMemo(()=>Math.random(),[]);
    const url = `${Configs.IMAGE_PREFIX}/camera/snapshot/${props.id}?v=${random}`;

    return (
        <Image
            className={props.className}
            src={url}
            alt=""
        />
    );
}

export function CameraRenderer(props: Props) {
    const cameraSet = useSetSelector(state => state.control.cameraList);
    const cameraBrandSet = useSetSelector(state => state.control.cameraBrandList);
    const selectedCamera = useMemo(() => cameraSet[props.id], [cameraSet, props.id]);
    const selectedCameraBrand = useMemo(() => selectedCamera && cameraBrandSet[selectedCamera.camera_brand_id], [cameraBrandSet, selectedCamera]);

    if (!selectedCamera || !selectedCameraBrand) {
        return null;
    }

    if (props.mode === 'snapshot' && (selectedCamera.rtsp || selectedCamera.mjpeg)) {
        return (
            <SnapshotRenderer
                {...props}
                brand={selectedCameraBrand}
                ip={selectedCamera.ip}
            />
        );
    }

    if (selectedCamera.video_cgi && (props.mode === 'video_cgi' || props.mode === 'auto' || props.mode === 'snapshot')) {
        return (
            <VideoCGIRenderer
                {...props}
                brand={selectedCameraBrand}
                ip={selectedCamera.ip}
                port={selectedCamera.video_cgi.port}
            />
        );
    }

    if (selectedCamera.mjpeg && (props.mode === 'mjpeg' || props.mode === 'auto')) {
        return (
            <MJPEGRenderer
                {...props}
                brand={selectedCameraBrand}
                ip={selectedCamera.ip}
                port={selectedCamera.mjpeg.port}
            />
        );
    }

    if (selectedCamera.rtsp && (props.mode === 'rtsp' || props.mode === 'auto')) {
        return (
            <RTSPRenderer
                {...props}
                brand={selectedCameraBrand}
                ip={selectedCamera.ip}
                port={selectedCamera.rtsp.port}
            />
        );
    }

    return null;
}
